// src/components/StorybookFAQ.js
import React from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// 🔹 Exportable FAQ items so EventPage can reuse them
export const STORYBOOK_FAQ_ITEMS = [
  {
    q: "What is a Storybook?",
    a: "Your Storybook is a soft-cover printed photobook created from the photos in this Elevate gallery. We design and arrange the pages for you so it feels like a true keepsake.",
  },
  {
    q: "How much does a Storybook cost?",
    a: "Pricing depends on how many photos are included in your Storybook. You’ll see the exact total during checkout. Shipping is a flat $5 anywhere in the U.S.",
  },
  {
    q: "Can I get a refund if I'm not happy?",
    a: "Because each Storybook is custom-designed and printed specifically for your event, refunds aren’t available. However, we’re confident you’ll love the final product — every book is professionally arranged to tell the story of your celebration beautifully.",
  },
  {
    q: "Can I remove photos I don’t want included?",
    a: "Yes. Hosts can delete any photo from the gallery at any time, and guests can delete the photos they personally uploaded.",
  },
  {
    q: "Who can order a Storybook?",
    a: "Once the gallery reaches the unlock threshold, both the host and guests can order their own copies. Each person checks out separately with their own shipping details.",
  },
  {
    q: "Where is it shipped?",
    a: "Each Storybook is shipped directly to the address you provide at checkout. Shipping is processed securely through Stripe — Elevate never sees your full payment details.",
  },
];

const StorybookFAQ = () => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f5f7",
        py: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              mb: 1,
              fontSize: { xs: "1.8rem", md: "2.1rem" },
            }}
          >
            Storybook FAQ
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", maxWidth: 420, mx: "auto" }}
          >
            Everything you need to know about turning your Elevate gallery into
            a printed photobook shipped straight to your door.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {STORYBOOK_FAQ_ITEMS.map((item, idx) => (
            <Accordion
              key={idx}
              disableGutters
              elevation={0}
              square={false}
              sx={{
                borderRadius: 2,
                "&::before": { display: "none" },
                border: "1px solid rgba(0,0,0,0.06)",
                bgcolor: "#fff",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  "& .MuiAccordionSummary-content": {
                    my: 0.5,
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, fontSize: "0.95rem" }}
                >
                  {item.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontSize: "0.9rem" }}
                >
                  {item.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", lineHeight: 1.4 }}
          >
            Storybook is powered by Elevate. Once your gallery has enough
            memories captured, you’ll see the option to continue to checkout
            from inside your event.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

// ✅ Both exports supported: default AND named.
export default StorybookFAQ;
export { StorybookFAQ };
